import { motion } from "framer-motion";
import { Gauge, Zap, Eye, Search, Shield, ArrowRight } from "lucide-react";
import { useState, useRef, useEffect } from "react";

const scores = [
  { label: "Performance", value: 98, color: "hsl(var(--primary))", icon: Zap, description: "Chargement instantané" },
  { label: "Accessibilité", value: 100, color: "hsl(var(--accent))", icon: Eye, description: "Accessible à tous" },
  { label: "Bonnes pratiques", value: 100, color: "hsl(var(--primary))", icon: Shield, description: "Standards Google" },
  { label: "SEO", value: 100, color: "hsl(var(--accent))", icon: Search, description: "Visible sur Google" },
];

function CircularGauge({ value, color, delay }: { value: number; color: string; delay: number }) {
  const [animatedValue, setAnimatedValue] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !hasAnimated.current) {
        hasAnimated.current = true;
        setTimeout(() => {
          const duration = 1200;
          const start = Date.now();
          const animate = () => {
            const elapsed = Date.now() - start;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setAnimatedValue(Math.round(eased * value));
            if (progress < 1) requestAnimationFrame(animate);
          };
          animate();
        }, delay);
      }
    }, { threshold: 0.5 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [value, delay]);

  const circumference = 2 * Math.PI * 54;
  const strokeDashoffset = circumference - (animatedValue / 100) * circumference;
  const gaugeColor = animatedValue >= 90 ? "#22c55e" : animatedValue >= 50 ? "#f59e0b" : "#ef4444";

  return (
    <div ref={ref} className="relative w-32 h-32">
      <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
        <circle cx="60" cy="60" r="54" fill="none" stroke="hsl(var(--border) / 0.3)" strokeWidth="8" />
        <circle
          cx="60" cy="60" r="54" fill="none"
          stroke={gaugeColor}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          style={{ transition: "stroke-dashoffset 0.1s ease-out" }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-3xl font-extrabold" style={{ color: gaugeColor }}>{animatedValue}</span>
      </div>
    </div>
  );
}

const competitors = [
  { name: "WordPress moyen", perf: 35, seo: 60 },
  { name: "Wix / Squarespace", perf: 45, seo: 55 },
  { name: "Freelance WordPress", perf: 50, seo: 65 },
  { name: "Folio Studio", perf: 98, seo: 100, highlight: true },
];

const SellPerformance = () => {
  return (
    <section className="py-24 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-accent/[0.02] to-transparent" />
      <div className="container mx-auto px-6 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
            <Gauge className="w-4 h-4" />
            Scores réels vérifiables
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-4">
            Des performances{" "}
            <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              mesurables
            </span>
          </h2>
          <p className="text-muted-foreground text-lg">
            Scores Google Lighthouse — testez vous-même sur{" "}
            <a href="https://pagespeed.web.dev/" target="_blank" rel="noopener" className="text-primary underline underline-offset-4 hover:text-accent transition-colors">
              PageSpeed Insights
            </a>
          </p>
        </motion.div>

        {/* Lighthouse Gauges */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto mb-20">
          {scores.map((score, i) => (
            <motion.div
              key={score.label}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="flex flex-col items-center text-center"
            >
              <CircularGauge value={score.value} color={score.color} delay={i * 200} />
              <h3 className="text-sm font-bold mt-4">{score.label}</h3>
              <p className="text-xs text-muted-foreground mt-1">{score.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Competitor comparison bars */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-2xl mx-auto"
        >
          <h3 className="text-lg font-bold mb-6 text-center">Vitesse de chargement comparée</h3>
          <div className="space-y-4">
            {competitors.map((comp, i) => (
              <motion.div
                key={comp.name}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="flex items-center gap-4"
              >
                <span className={`text-sm w-44 flex-shrink-0 text-right ${comp.highlight ? "font-bold text-primary" : "text-muted-foreground"}`}>
                  {comp.name}
                </span>
                <div className="flex-1 h-8 bg-secondary/50 rounded-lg overflow-hidden relative">
                  <motion.div
                    className={`h-full rounded-lg ${comp.highlight ? "bg-gradient-to-r from-primary to-accent" : "bg-muted-foreground/30"}`}
                    initial={{ width: 0 }}
                    whileInView={{ width: `${comp.perf}%` }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 + i * 0.15, duration: 0.8, ease: "easeOut" }}
                  />
                  <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold ${comp.highlight ? "text-primary-foreground" : "text-muted-foreground"}`}>
                    {comp.perf}/100
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
          <p className="text-center text-xs text-muted-foreground mt-4">
            * Scores moyens Google Lighthouse — Mars 2026
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default SellPerformance;
